import { Typography } from '@evaneos/b2b-ui';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { DisplayProject } from '../../../../resources/dossier-overview/types';
import { useUpdateDatesOfServicesMutation } from '../../../../resources/dossier/mutations';
import { DossierId } from '../../../../resources/dossier/types';
import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import { getToday } from '../../../../shared/utilities/date';
import { DateRangeFromInputs } from '../../../../design-system/Form/DateRangeFromInputs';
import { DateRange } from '../../../../design-system/Form/types';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

type TravelDatesEditableItemProps = {
    project: DisplayProject | undefined;
    dossierId: DossierId;
    labelId: string;
    disabled: boolean;
};

export function TravelDatesEditableItem({
    project,
    dossierId,
    labelId,
    disabled,
}: Readonly<TravelDatesEditableItemProps>) {
    const { formatMessage, formatDate } = useIntl();
    const today = getToday();

    const updateDatesMutation = useUpdateDatesOfServicesMutation(dossierId);
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const [datesOfServices, setDatesOfServices] = useState<DateRange>({
        from: project?.expectedArrivalDate ?? undefined,
        to: project?.departureDate ?? undefined,
    });

    const [isDatesOfServicesValid, setDatesOfServicesValid] = useState<boolean>(true);

    const handleRangeChange = (range: DateRange) => {
        setDatesOfServices(range);

        const startDateIsAfterOrEqualToday =
            !!range.from && (isSameDay(range.from, today) || isAfter(range.from, today));
        const startDateIsBeforeOrEqualEndDate =
            !!range.from &&
            !!range.to &&
            (isSameDay(range.from, range.to) || isBefore(range.from, range.to));

        setDatesOfServicesValid(startDateIsAfterOrEqualToday && startDateIsBeforeOrEqualEndDate);
    };

    const handleBeforeClose = () => {
        if (!project) return true;

        if (!isDatesOfServicesValid) {
            return false;
        }

        const isDateOfServicesHaveChanged =
            datesOfServices.from?.getTime() !== project.expectedArrivalDate?.getTime() ||
            datesOfServices.to?.getTime() !== project.departureDate?.getTime();

        if (
            datesOfServices.from &&
            datesOfServices.to &&
            isDatesOfServicesValid &&
            isDateOfServicesHaveChanged
        ) {
            updateDatesMutation.mutate({
                startDate: datesOfServices.from,
                endDate: datesOfServices.to,
            });

            trackEvent((ampli, defaultProperties) => {
                ampli.mainInformationChanged({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    edited_field: 'trip dates',
                });
            });
        }

        return true;
    };

    return (
        <DefinitionList.EditableValue
            labelId={labelId}
            placement="below"
            popoverWidth="fit-content"
            onBeforeClose={handleBeforeClose}
            disabled={disabled}
            disabledTooltip={
                disabled
                    ? formatMessage({ id: 'dossier.information.disabled.contract_tooltip' })
                    : undefined
            }
            editor={() => (
                <DateRangeFromInputs
                    initialStartDate={project?.expectedArrivalDate ?? undefined}
                    initialEndDate={project?.departureDate ?? undefined}
                    onDateRangeChange={handleRangeChange}
                    startDateLabel={formatMessage({ id: 'dossier.project.arrivalDate' })}
                    endDateLabel={formatMessage({ id: 'dossier.project.departureDate' })}
                    fixedWeeks
                    containerSx={{
                        p: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        flexDirection: { xs: 'column', lg: 'row' },
                        gap: 1,
                    }}
                    inputWrapperSx={{
                        '& label': {
                            fontSize: '1.4rem',
                        },
                        '& input': {
                            fontSize: '1.4rem',
                        },
                    }}
                />
            )}
        >
            <Typography variant="body2" component="span">
                {project?.expectedArrivalDate || project?.departureDate ? (
                    <>
                        {project?.expectedArrivalDate
                            ? formatDate(project.expectedArrivalDate, {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                              })
                            : '—'}
                        {' → '}
                        {project?.departureDate
                            ? formatDate(project.departureDate, {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                              })
                            : '—'}
                        {project?.duration && (
                            <Typography component="span" color="text.secondary" variant="body2">
                                {' '}
                                (
                                {formatMessage(
                                    {
                                        id: project?.arrivalDate
                                            ? 'project.duration'
                                            : 'project.durationApproximate',
                                    },
                                    { days: project.duration },
                                )}
                                )
                            </Typography>
                        )}
                    </>
                ) : (
                    <Typography component="span" color="text.secondary" variant="body2">
                        —
                    </Typography>
                )}
            </Typography>
        </DefinitionList.EditableValue>
    );
}
